import React, { useCallback } from "react";
import { Edit, PictureInPictureOutlined, Settings } from "@mui/icons-material";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";

import { useGetVersionQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { loadProject } from "fond/project/redux";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  clean: { label: "Cleaning data" },
  ug_network: { label: "Generating underground network" },
  leadins: { label: "Generating leadins" },
  outputs: { label: "Preparing outputs" },
};

interface DataPrepPanelProps {
  project: Project;
  versionId: string;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ project, versionId, readOnly }: DataPrepPanelProps) => {
  const dispatch = useAppDispatch();
  const { open } = useStackedNavigationContext();
  const { data: version } = useGetVersionQuery(versionId ?? "", { skip: !versionId });
  const { layerKeys } = useGetPlannerLayers(versionId);
  const { value: canReviewProblematicParcels } = useFeatureFlag("street_parcel_identification");

  const addressLayer = layerKeys.includes("input_layers/demand");
  const undergroundLayer = layerKeys.includes("input_layers/streets");

  const handleOnComplete = useCallback(async () => {
    // TODO: we should probably go to the 'Design' step automagically once this finishes?
    await dispatch(loadProject({ uuid: project.ID, forceRefetch: true }));
  }, [dispatch, project.ID]);

  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP,
    onComplete: handleOnComplete,
  });

  const isDataPrepping = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const canDataPrep =
    addressLayer && undergroundLayer && Boolean(runWorkflow) && !isDataPrepping && Boolean(version?.Architecture?.DataPrepConfiguration);

  const handleOpenSettings = () => {
    open("dataPrepSettings");
  };

  const handleDataPrep = async () => {
    if (!runWorkflow) {
      return;
    }
    runWorkflow();
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("extended-design-modifications");
  };

  const handleIdentifyStreetParcels = () => {
    open("identify-street-parcels");
  };

  return (
    <>
      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          Data could not be generated.
        </Alert>
      )}

      {!isDataPrepping && (
        <>
          {canReviewProblematicParcels && (
            <StackedNavigationButton
              data-testid="identify-street-parcels-navigation"
              icon={<PictureInPictureOutlined />}
              title="Review problematic parcels"
              onClick={handleIdentifyStreetParcels}
              disabled={readOnly}
            />
          )}

          <StackedNavigationButton
            data-testid="data-prep-settings-navigation"
            icon={<Settings />}
            title="Settings"
            onClick={handleOpenSettings}
            disabled={readOnly}
            sx={{ mb: 1 }}
          />

          <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!canDataPrep || readOnly} onClick={handleDataPrep}>
            {workflowState === "COMPLETE" ? "Re-run data preparation" : "Run data preparation"}
          </Button>
        </>
      )}

      {stages && (isDataPrepping || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {isDataPrepping && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || readOnly} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {workflowState === "COMPLETE" && !isDataPrepping && (
        <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} disabled={readOnly} sx={{ m: 0, mt: 1 }} />
      )}
    </>
  );
};

export default DataPrepPanel;
